import { useEffect } from "react";
import { times } from "lodash-es";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Icon, Link, Text, Tooltip, Wrap } from "@chakra-ui/react";
import { Link as RemixLink } from "@remix-run/react";
import { FaGift as GiftIcon } from "react-icons/fa";

import { HelpPopover } from "#app/components/index.jsx";
import { maybePluralizeNoun } from "#app/utils/words.js";
import {
  useOptionalUser,
  useUserAvailabilityRequestLimit,
} from "#app/hooks/index.js";

// Renders a single cell in the request-cap grid. Free-access "bonus" cells
// are prepended to the grid so they flow with the squares instead of breaking
// out into a separate group, which got noisy at scale.
export function AvailabilityRequestLimitVisual({
  counted,
  freeAccess,
  ...rest
}) {
  const { limit } = useUserAvailabilityRequestLimit();

  const freeAccessCount = freeAccess ?? 0;
  const itemCount = Math.max(limit, counted);

  const giftItems = times(freeAccessCount, (i) => (
    <Box
      key={`free-access-${i}`}
      h="4"
      w="4"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="warning"
      display="flex"
      alignItems="center"
      justifyContent="center"
      data-testid="free-access-bonus-cell"
    >
      <Icon as={GiftIcon} boxSize="11px" color="warning" aria-hidden="true" />
    </Box>
  ));

  const slotItems = times(itemCount, (i) => {
    const n = i + 1;
    const isInBounds = n <= counted && n <= limit;
    const isOOB = n > limit;

    return (
      <Box
        key={i}
        h="4"
        w="4"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={
          isInBounds
            ? "success-on-canvas"
            : isOOB
              ? "warning-on-canvas"
              : "bg-emphasized"
        }
        bg={
          isInBounds
            ? "success-on-canvas"
            : isOOB
              ? "warning-on-canvas"
              : undefined
        }
      ></Box>
    );
  });

  const tooltipLabel =
    freeAccessCount > 0
      ? `${maybePluralizeNoun(counted, "active request")} of ${limit} (+${maybePluralizeNoun(freeAccessCount, "free request")})`
      : `You have ${maybePluralizeNoun(counted, "active request")}`;

  return (
    <Tooltip label={tooltipLabel}>
      <Box {...rest}>
        <Wrap spacing="1">
          {giftItems}
          {slotItems}
        </Wrap>
      </Box>
    </Tooltip>
  );
}

const queryKey = ["availability-request-limit-details"];

export function AvailabilityRequestLimitDetails({
  counted,
  freeAccess,
  text = "You can have",
  textProps = {},
  limitText = "You're watching at max capacity. ",
  limitTextProps = {},
  popoverProps = {},
  ...rest
}) {
  const { limit: maxRequests } = useUserAvailabilityRequestLimit();
  const queryClient = useQueryClient();

  const shouldFetch = counted === undefined;

  const { data: fetched, isPending } = useQuery({
    queryKey,
    queryFn: async () => fetch(`/api/browse`).then((res) => res.json()),
    enabled: shouldFetch,
    select: (data) => {
      if (typeof data === "number") {
        return { counted: data, freeAccess: 0 };
      }
      return {
        counted: data.countedActive ?? data.active?.length ?? 0,
        freeAccess: data.freeAccessActive ?? 0,
      };
    },
  });

  const currentCounted = counted !== undefined ? counted : fetched?.counted;

  useEffect(() => {
    if (counted !== undefined) {
      queryClient.setQueryData(queryKey, {
        counted,
        freeAccess: freeAccess ?? 0,
      });
    }
  }, [counted, freeAccess, queryClient]);

  const isLoadingCount = isPending && shouldFetch;
  const remainingRequests = isLoadingCount ? "_" : maxRequests - currentCounted;
  const hasReachedLimit = !isLoadingCount && remainingRequests <= 0;

  if (hasReachedLimit) {
    return (
      <Box {...rest} data-testid="availability-request-limit-details">
        <Text {...textProps} suppressHydrationWarning={true}>
          <Text as="strong" {...limitTextProps}>
            {limitText}
          </Text>
          <AvailabilityRequestLimitIncreaseText
            as="span"
            popoverProps={popoverProps}
            {...textProps}
          />
        </Text>
      </Box>
    );
  }

  return (
    <Box {...rest} data-testid="availability-request-limit-details">
      <Text {...textProps} suppressHydrationWarning={true}>
        {text} <strong>{remainingRequests} more </strong>
        <ActiveRequestHelpPopover
          {...popoverProps}
          buttonText={maybePluralizeNoun(
            remainingRequests,
            "active request",
            "s",
            false,
          )}
        />
      </Text>
    </Box>
  );
}

export function ActiveRequestHelpPopover({
  buttonText = "active requests",
  ...rest
}) {
  const user = useOptionalUser();

  return (
    <HelpPopover
      buttonText={buttonText}
      body={
        <>
          <Text>
            An active request is{" "}
            <strong>currently watching for availability</strong>.
          </Text>
          <Text mt={4}>
            These are listed on{" "}
            {user ? (
              <Link as={RemixLink} to="/browse">
                your dashboard
              </Link>
            ) : (
              <Text as="span">your dashboard</Text>
            )}
            , and can be paused to make room for a new request.
          </Text>
          <Text mt={4} color="muted" fontSize="sm">
            Free requests don&rsquo;t count toward this limit — they&rsquo;re
            bonus slots on top.
          </Text>
        </>
      }
      {...rest}
    />
  );
}

export function AvailabilityRequestLimitIncreaseText({
  popoverProps,
  ...rest
}) {
  const { canSubscribe } = useUserAvailabilityRequestLimit();

  if (canSubscribe) {
    return (
      <Text {...rest}>
        For more <ActiveRequestHelpPopover {...popoverProps} />,{" "}
        <Link as={RemixLink} to="/subscribe">
          upgrade your account
        </Link>
        .
      </Text>
    );
  }

  return (
    <Text {...rest}>
      To create another request, you must pause an existing one.
    </Text>
  );
}
